/*
 * Copyright [ 2020 - 2024 ] [Matthew Buckton]
 * Copyright [ 2024 - 2025 ] [Maps Messaging B.V.]
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

"use client";

import "@/styles/globals.css";
import {AppProps} from "next/app";
import {Auth0Provider, useAuth0} from "@auth0/auth0-react";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {useEffect, useState} from "react";
import Head from "next/head";
import axios from "axios";
import Layout from "@/component/Layout";

axios.defaults.baseURL = process.env.API_BASE_URL;

function AttachAuthToken() {
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();

    useEffect(() => {
        async function setAuthToken() {
            if (isAuthenticated) {
                try {
                    const token = await getAccessTokenSilently();
                    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
                } catch (error) {
                    console.error("Failed to fetch token:", error);
                }
            }
        }
        setAuthToken();
    }, [getAccessTokenSilently, isAuthenticated]);

    return null;
}

export default function MyApp({ Component, pageProps }: AppProps) {
    const [queryClient] = useState(() => new QueryClient());

    return (
        <Auth0Provider
            domain="dev-dr3rzpbx6mqtxzeu.us.auth0.com"
            clientId="ffKMlL4Ub2K4NpXOhngOED74jtAP0BzP"
            authorizationParams={{
                redirect_uri: typeof window !== "undefined" ? window.location.origin : undefined,
                audience: "https://license.mapsmessaging.io",
                scope: "openid profile email",
            }}
        >
            <Head>
                <title>MapsMessaging License Manager</title>
                <link rel="stylesheet" href="/styles/globals.css" /> {/* ✅ Fix for static export */}
            </Head>
            <QueryClientProvider client={queryClient}>
                <AttachAuthToken />
                <Layout>
                    <Component {...pageProps} />
                </Layout>
            </QueryClientProvider>
        </Auth0Provider>
    )
}
